/**
 * TextField - Text input field component
 *
 * Task: T-P4-D2-F4b-4
 * Feature: P4-D2-F4 Web App Forms UI
 *
 * Standard text input with label, help text, and error display.
 * Supports minLength, maxLength, and pattern validation.
 */

import type { ChangeEvent } from 'react';
import type { TextFieldProps } from './types';
import { FieldWrapper } from './FieldWrapper';
import { formatUsPhone, isPhoneField } from './formatPhone';
import styles from './fields.module.css';

/**
 * TextField renders a standard text input with FieldWrapper.
 */
export function TextField({
  field,
  value,
  onChange,
  onBlur,
  error,
  style,
  nonInteractive,
}: TextFieldProps) {
  // Phone fields (by id/label) auto-format to (123) 456-7890 as the user types.
  const phone = isPhoneField(field.fieldId, field.label);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    onChange(phone ? formatUsPhone(raw) : raw);
  };

  return (
    <FieldWrapper
      fieldId={field.fieldId}
      label={field.label}
      required={field.required}
      helpText={field.helpText}
      error={error}
      // A phone field is bounded by its mask, never by a character count: no counter (Gabriel 2026-09-03).
      maxLength={phone ? undefined : field.maxLength}
      currentLength={value.length}
      style={style}
    >
      <input
        type={phone ? 'tel' : 'text'}
        inputMode={phone ? 'tel' : undefined}
        autoComplete={phone ? 'tel' : undefined}
        id={field.fieldId}
        name={field.fieldId}
        className={`${styles.input} ${error ? styles.hasError : ''}`}
        value={value}
        onChange={handleChange}
        onBlur={onBlur}
        placeholder={field.placeholder}
        maxLength={field.maxLength}
        minLength={field.minLength}
        readOnly={nonInteractive}
        aria-invalid={!!error}
        aria-describedby={error ? `${field.fieldId}-error` : undefined}
      />
    </FieldWrapper>
  );
}
